// SDK image/video errors wrap originalError; REST/ChatJobError also expose
// payload.error.subscription. Keep those live protocol fields through UI copy.
export function getGenerationErrorDetails(error) {
  const sources = [];
  const pending = [error];
  const seen = new Set();
  while (pending.length) {
    const item = pending.shift();
    if (!item || typeof item !== 'object' || seen.has(item)) continue;
    seen.add(item);
    sources.push(item);
    pending.push(item.originalError, item.cause, item.payload, item.error, item.subscription, item.details, item.errorData);
  }
  // A cleared field (null) falls through to the next source: a recovered tile
  // carries `code: null` beside its `errorCode`.
  const first = (...keys) => {
    for (const source of sources) {
      for (const key of keys) if (source[key] !== undefined && source[key] !== null) return source[key];
    }
  };
  return {
    code: first('code', 'errorCode', 'error_code', 'subscriptionErrorCode'),
    originalCode: first('originalCode'),
    message: error?.originalError?.message || first('message', 'error_message') || (typeof error === 'string' ? error : ''),
    errorType: first('errorType', 'type'),
    subscriptionLimit: first('subscriptionLimit'),
    requiredPlans: first('requiredPlans'),
    feature: first('feature'),
    limitation: first('limitation'),
    kind: first('kind'),
    currency: first('currency'),
    required: first('required'),
    available: first('available'),
    request: first('request'),
    quote: first('quote'),
    status: first('status'),
    details: first('details'),
    retryAfter: first('retryAfter'),
    jobID: first('jobID'),
    projectId: first('projectId'),
    unverified: first('unverified'),
    vendorFailureCategory: first('vendorFailureCategory'),
    isNSFWFiltered: first('isNSFWFiltered')
  };
}

export function copyGenerationErrorDetails(target, source) {
  for (const [key, value] of Object.entries(getGenerationErrorDetails(source))) {
    if (key !== 'message' && value !== undefined) target[key] = value;
  }
  target.cause = source;
  return target;
}

export function isSilentGenerationError(error) {
  return ['AUTH_REQUIRED', 'COST_APPROVAL_CANCELED'].includes(String(getGenerationErrorDetails(error).code));
}

// Code contracts: sogni-socket/constants/errorCodes.js and
// sogni-api/src/error-handling/errorcodes.ts (Spark, LLM and subscription).
export function classifyGenerationFailure(error) {
  const details = getGenerationErrorDetails(error);
  const code = String(details.code || /\b(?:code\s*)?(4\d{3})\b/i.exec(details.message || '')?.[1] || '');
  const message = `${details.errorType || ''} ${details.message || ''}`.toLowerCase();
  const feature = details.feature || '';
  if (['AUTH_REQUIRED', 'SESSION_IDENTITY_CHANGED', 'SESSION_ENDED', '4021'].includes(code)) return 'auth';
  // The prompt classifier's child-safety decline is final: no retry, no filter hint.
  if (details.originalCode === 'promptSafetyDeclined') return 'declined';
  // Withheld by the filter (result), or refused at admission (prompt). A
  // `sensitiveContent` refusal is the filter only with the socket's default
  // text: a vendor model that takes no sensitive prompts refuses them whatever
  // the filter says, with its own text naming an alternative, and that stays
  // 'other' so it is shown as sent with no switch hint.
  if (code === 'NSFW_FILTERED' || details.isNSFWFiltered === true
    || /sensitive content detected|filtered \(nsfw\)/.test(message)) return 'filtered';
  if (['4027', '4029', '4030', '4079'].includes(code)) return 'queue';
  if (code === '4053' || /depr[ei]cat|retired/.test(message)) return 'model-retired';
  if (code === 'SELECTED_MODEL_UNAVAILABLE') return 'model-unavailable';
  if (code === '4080') return 'billing-recovery';
  if (['4087', '4089'].includes(code) || ['daily_fair_use', 'monthly_fair_use'].includes(feature)) return 'fair-use';
  if (['4071', '4072'].includes(code)) return 'request-cost';
  if (['4085', '4086'].includes(code)) return 'payment-verification';
  if (['4066', '4067', '4068', '4069', '4070'].includes(code)) return 'free-limits';
  if (['4078', '179'].includes(code) || details.errorType === 'subscription_unavailable') return 'entitlement';
  if (['4081', '178'].includes(code) || details.subscriptionLimit === true) {
    return feature.startsWith('trial_') ? 'trial-limit' : 'plan-limit';
  }
  if (['4054', '4055', '4062', '4063', '4088'].includes(code)) return 'spark-tier';
  if (['INSUFFICIENT_CREDITS', '4024', '123', '174', '14'].includes(code)
    || details.errorType === 'insufficient_balance' || details.kind === 'balance') return 'balance';
  if (details.kind === 'spark-tier') return 'spark-tier';
  // Older transport wrappers only carry text. Exclude queue/limit/availability
  // wording before their "Premium Spark remains available" guidance.
  if (/queue.*full|maximum.*jobs.*queue/.test(message)) return 'queue';
  // Studio's banner gets only the socket's 4087/4089 refusal copy, which also
  // names Premium Spark; its "used N%" progress notes are not refusals.
  if (/used up your plan.s (?:daily |monthly )?fair-use capacity/.test(message)) return 'fair-use';
  if (/requires? (?:payment.*)?premium spark|code 4054|free spark.*not supported/.test(message)) return 'spark-tier';
  if (/insufficient(?:_| )(?:spark |premium spark )?(?:balance|funds)|not enough spark/.test(message)) return 'balance';
  return 'other';
}
